import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../../../environments/environment';
import { Alert, AlertFormData, AlertSearchPayload, AlertSearchResponse } from '../models/alerts';

@Injectable({
  providedIn: 'root'
})
export class AlertsService {
  readonly defaultConfig: Required<AlertSearchPayload> = {
    search: null
  };

  private http = inject(HttpClient);

  // The desktop table has no paginator — DEV-1165 replaced server-side paging with
  // CdkVirtualScrollViewport (see AlertVirtualScrollDataSource), which virtualizes rendering of
  // whatever the response contains rather than fetching one page at a time. Paging was later
  // removed entirely from the SQL/API contract (not just made nullable), so there's no
  // pageNumber/pageSize to send at all now. Sorting is likewise not sent — the grid sorts
  // client-side (see AlertsComponent.onSort) rather than round-tripping to reorder rows already
  // sitting in the browser.
  search({ search }: Partial<AlertSearchPayload>): Observable<AlertSearchResponse> {
    const body = {
      search: search ?? this.defaultConfig.search
    };
    return this.http.post<AlertSearchResponse>(`${environment.apiUrl}/alerts/search`, body);
  }

  getById(id: number): Observable<Alert> {
    return this.http.get<Alert>(`${environment.apiUrl}/alerts/${id}`);
  }

  create(data: AlertFormData): Observable<Alert> {
    return this.http.post<Alert>(`${environment.apiUrl}/alerts`, data);
  }

  update(id: number, data: AlertFormData): Observable<Alert> {
    return this.http.put<Alert>(`${environment.apiUrl}/alerts/${id}`, data);
  }

  setActive(id: number, isActive: boolean): Observable<Alert> {
    return this.http.patch<Alert>(`${environment.apiUrl}/alerts/${id}/active`, { isActive });
  }

  delete(id: number) {
    return this.http.delete(`${environment.apiUrl}/alerts/${id}`);
  }

  // Backs the admin "End-user preview" simulation — currently-live alerts only.
  getLive(): Observable<Alert[]> {
    return this.http.get<Alert[]>(`${environment.apiUrl}/alerts/live`);
  }
}
